<template>
  <div class="app-chrome" :class="{ night }">
    <!-- 子游戏顶栏：返回 + 彩种名（大厅页不显示） -->
    <header v-if="showTitleBar" class="title-bar">
      <button class="back-btn" type="button" aria-label="返回" @click="onBack">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M14.8 5.2 8.5 12l6.3 6.8"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <h1 class="title-text">{{ titleText }}</h1>
      <span class="title-spacer" aria-hidden="true" />
    </header>

    <header class="header-nav">
      <nav class="nav-list">
        <div
          v-for="item in navList"
          :key="item.name"
          class="nav-item"
          :class="{ active: activeName === item.name }"
          @click="go(item)"
        >
          <span class="nav-text">{{ item.name }}</span>
        </div>
      </nav>
      <div class="nav-actions">
        <button class="nav-icon sun" type="button" aria-label="主题" @click="toggleNight">
          <img :src="night ? moonIcon : sunIcon" alt="" />
        </button>
        <button class="nav-icon search" type="button" aria-label="搜索" @click="openSearch">
          <img :src="searchIcon" alt="" />
        </button>
      </div>
    </header>
  </div>

  <Teleport to="body">
    <div v-if="searchOpen" class="search-page" :class="{ night }">
      <div class="search-top">
        <div class="search-input-wrap">
          <svg class="search-ico" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M15.85 15.85a7.5 7.5 0 1 0-1.06 1.06l4.24 4.24a.75.75 0 0 0 1.06-1.06l-4.24-4.24ZM4.5 10.5a6 6 0 1 1 12 0 6 6 0 0 1-12 0Z"
            />
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchKeyword"
            class="search-input"
            type="search"
            enterkeyhint="search"
            placeholder="输入彩种名，直接开始游戏"
            @keydown.esc="closeSearch"
            @keydown.enter.prevent="onSearchEnter"
          />
          <button
            v-if="searchKeyword"
            class="clear-btn"
            type="button"
            aria-label="清除"
            @click="clearKeyword"
          >
            <span class="clear-x" />
          </button>
        </div>
        <button class="cancel-btn" type="button" @click="closeSearch">取消</button>
      </div>

      <div class="search-body">
        <template v-if="!hasKeyword">
          <div class="hot-title">热门搜索</div>
          <div class="hot-grid">
            <button
              v-for="(item, idx) in HOT_SEARCH"
              :key="item"
              type="button"
              class="hot-item"
              @click="pickHot(item)"
            >
              <span class="hot-rank" :class="{ top: idx < 4 }">{{ idx + 1 }}</span>
              <span class="hot-name">{{ item }}</span>
            </button>
          </div>
        </template>

        <template v-else>
          <div class="result-list">
            <button
              v-for="g in searchResults"
              :key="g.id"
              type="button"
              class="result-item"
              @click="goSearchGame(g.id)"
            >
              <img v-if="g.icon" :src="g.icon" alt="" />
              <span>{{ g.name }}</span>
            </button>
            <p v-if="!searchResults.length" class="search-empty">没有匹配的彩种</p>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import searchIcon from '@/assets/lobby/ui/search.svg'
import sunIcon from '@/assets/lobby/ui/header-sun.svg'
import moonIcon from '@/assets/lobby/ui/header-moon.svg'
import { useTheme } from '@/composables/useTheme'
import { useActiveGame, setActiveGame, syncGameFromQuery } from '@/composables/useActiveGame'
import { getBetPath, getTrendPath } from '@/composables/betRoute'
import { visibleGames } from '@/data/games'
import { goHelalaHall } from '@/api/session'

const HOT_SEARCH = [
  '澳门飞艇',
  '六合彩',
  '天成快三',
  '天成快乐⑧',
  '福彩3D',
  '双色球',
  '大乐透'
]

const router = useRouter()
const route = useRoute()
const { night, toggleNight } = useTheme()
const { gameId, currentGame } = useActiveGame()
const searchOpen = ref(false)
const searchKeyword = ref('')
const searchInputRef = ref(null)

/** 子游戏页显示白底标题栏；彩票大厅 `/` 不显示。
 *  嵌在 Helala /gameDetail iframe 时，外层已有返回标题栏，避免双层白顶。 */
const showTitleBar = computed(() => {
  if (route.path === '/') return false
  try {
    if (window.top && window.top !== window) return false
  } catch (_) {
    return false
  }
  return true
})
const titleText = computed(() => currentGame.value?.name || '彩票')

const onBack = () => {
  // 子页返回：一律回 Helala 主站娱乐大厅（图二）；进入游戏逻辑不变
  goHelalaHall()
}

watch(
  () => route.query.id,
  (id) => {
    if (id != null && id !== '') syncGameFromQuery(id)
  },
  { immediate: true }
)

const hasKeyword = computed(() => !!searchKeyword.value.trim())

const searchResults = computed(() => {
  const q = searchKeyword.value.trim().toLowerCase()
  const list = visibleGames.value || []
  if (!q) return []
  return list.filter((g) => String(g.name).toLowerCase().includes(q)).slice(0, 40)
})

const openSearch = async () => {
  searchKeyword.value = ''
  searchOpen.value = true
  document.body.style.overflow = 'hidden'
  await nextTick()
  searchInputRef.value?.focus()
}

const closeSearch = () => {
  searchOpen.value = false
  searchKeyword.value = ''
  document.body.style.overflow = ''
}

const clearKeyword = () => {
  searchKeyword.value = ''
  nextTick(() => searchInputRef.value?.focus())
}

const pickHot = (kw) => {
  searchKeyword.value = kw
  nextTick(() => searchInputRef.value?.focus())
}

const goSearchGame = (id) => {
  closeSearch()
  setActiveGame(id)
  router.push({ path: getBetPath(id), query: { id } })
}

const onSearchEnter = () => {
  const first = searchResults.value[0]
  if (first) goSearchGame(first.id)
}

const navList = [
  { name: '大厅', path: '/', role: 'lotto-lobby' },
  { name: '分类', path: '/category' },
  { name: '投注', path: '/bet' },
  { name: '走势', path: '/trend' },
  { name: '记录', path: '/record' }
]

const activeName = computed(() => {
  if (route.path === '/category') return '分类'
  if (
    route.path === '/bet' ||
    route.path === '/bet/tc-lhc' ||
    route.path === '/bet/tc-k3' ||
    route.path === '/bet/tc-keno8' ||
    route.path === '/bet/fc3d' ||
    route.path === '/bet/dlt' ||
    route.path === '/bet/ssq' ||
    route.path.startsWith('/fastThree/')
  ) return '投注'
  if (route.path === '/trend' || route.path === '/trend/tc-lhc' || route.path === '/trend/tc-keno8') return '走势'
  if (route.path === '/record') return '记录'
  // SSO 进彩票默认落在 `/`，高亮「大厅」
  return '大厅'
})

const betPath = computed(() => getBetPath(gameId.value))
const trendPath = computed(() => getTrendPath(gameId.value))

const go = (item) => {
  // 「大厅」= 彩票大厅页（8080 /），同域 lgw-token 自然保留
  if (item.role === 'lotto-lobby') {
    if (route.path === '/') return
    router.push({ path: '/' })
    return
  }
  const withGame = item.path === '/bet' || item.path === '/trend' || item.path === '/record'
  if (withGame) {
    const path = item.path === '/bet' ? betPath.value : item.path === '/trend' ? trendPath.value : item.path
    if (route.path === path && Number(route.query.id) === gameId.value) return
    router.push({ path, query: { id: gameId.value } })
    return
  }
  if (route.path !== item.path) router.push(item.path)
}
</script>

<style scoped>
.app-chrome {
  position: sticky;
  top: 0;
  z-index: 40;
}

.title-bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  padding: 0 10px;
  background: #fff;
  border-bottom: 1px solid #f42c4c;
}

.back-btn {
  position: relative;
  z-index: 1;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #f0f0f0;
  color: #333;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.back-btn svg {
  width: 18px;
  height: 18px;
  display: block;
}

.title-text {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 48px;
  pointer-events: none;
  z-index: 0;
}

.title-spacer {
  position: relative;
  z-index: 1;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.app-chrome.night .title-bar {
  background: #1e1f24;
  border-bottom-color: #f42c4c;
}

.app-chrome.night .back-btn {
  background: #2c2e36;
  color: #eee;
}

.app-chrome.night .title-text {
  color: #f5f5f5;
}

.header-nav {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  padding: 8px 8px 12px 4px;
  background: #f42c4c;
}

.nav-list {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  min-width: 0;
}

.nav-item {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 0 8px;
  cursor: pointer;
}

.nav-text {
  position: relative;
  font-size: 15px;
  line-height: 1;
  color: #fff;
  font-weight: 500;
  white-space: nowrap;
}

.nav-item.active .nav-text {
  font-size: 17px;
  font-weight: 700;
}

.nav-item.active .nav-text::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -5px;
  height: 3px;
  border-radius: 2px;
  background: #fff;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding-left: 4px;
}

.nav-icon {
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.nav-icon.sun {
  width: 24px;
  height: 24px;
  border-radius: 5px;
  overflow: hidden;
}

.nav-icon.search {
  width: 28px;
  height: 28px;
  padding: 5px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.16);
  box-sizing: border-box;
}

.nav-icon img,
.nav-icon svg {
  width: 100%;
  height: 100%;
  display: block;
}

.search-page {
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  flex-direction: column;
  background: #f42c4c;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
}

.search-top {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px 14px;
  flex-shrink: 0;
}

.search-input-wrap {
  flex: 1;
  min-width: 0;
  height: 36px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px 0 12px;
  background: #fff;
  border-radius: 18px;
}

.search-ico {
  width: 18px;
  height: 18px;
  color: #b0b0b0;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #333;
  appearance: none;
}

.search-input::placeholder {
  color: #b8b8b8;
}

.search-input::-webkit-search-cancel-button,
.search-input::-webkit-search-decoration {
  -webkit-appearance: none;
  appearance: none;
}

.clear-btn {
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #c8c8c8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.clear-x {
  position: relative;
  width: 8px;
  height: 8px;
}

.clear-x::before,
.clear-x::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 0;
  width: 1.5px;
  height: 8px;
  background: #fff;
  border-radius: 1px;
}

.clear-x::before {
  transform: translateX(-50%) rotate(45deg);
}

.clear-x::after {
  transform: translateX(-50%) rotate(-45deg);
}

.cancel-btn {
  flex-shrink: 0;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 15px;
  padding: 0 2px;
  cursor: pointer;
  line-height: 1;
}

.search-body {
  flex: 1;
  min-height: 0;
  background: #fff;
  border-radius: 16px 16px 0 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 14px 24px;
}

.hot-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 14px;
}

.hot-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  column-gap: 6px;
  row-gap: 16px;
}

.hot-item {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  line-height: 1.2;
}

.hot-rank {
  flex-shrink: 0;
  font-size: 13px;
  color: #c0c0c0;
  font-variant-numeric: tabular-nums;
}

.hot-rank.top {
  color: #f42c4c;
  font-weight: 600;
}

.hot-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-list {
  display: flex;
  flex-direction: column;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 2px;
  border: 0;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  font-size: 15px;
  color: #333;
  text-align: left;
  cursor: pointer;
}

.result-item img {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
}

.search-empty {
  margin: 40px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* 搜索页深色：红顶栏保留，内容区黑底 */
.search-page.night .search-ico {
  color: #f42c4c;
}

.search-page.night .search-body {
  background: #1e1f24;
}

.search-page.night .hot-title {
  color: #fff;
}

.search-page.night .hot-item {
  color: #c8c8c8;
}

.search-page.night .hot-rank {
  color: #c8c8c8;
}

.search-page.night .hot-rank.top {
  color: #f42c4c;
}

.search-page.night .result-item {
  background: #1e1f24;
  border-bottom-color: #333;
  color: #ddd;
}

.search-page.night .search-empty {
  color: #888;
}
</style>
